import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

const EASE = [0.16, 1, 0.3, 1];

/* ─────────────────────────────────────────────────────────────
   A confirmation that floats just under the top bar.

   useFlash's line sits in a screen's flow, which is right for what a
   screen changes. The bar has no flow of its own, so what it changes
   (a scene, mostly) is confirmed here instead, over whatever screen
   is open. Same shape as useFlash: [node, show].
   ───────────────────────────────────────────────────────────── */
export function useToast() {
    const [toast, setToast] = useState(null);
    const timer = useRef(null);
    const reduced = useReducedMotion();

    useEffect(() => () => clearTimeout(timer.current), []);

    const show = useCallback((text, { tone = 'info', swatch } = {}) => {
        clearTimeout(timer.current);
        setToast({ text, tone, swatch, key: Date.now() });
        timer.current = setTimeout(() => setToast(null), 4200);
    }, []);

    if (typeof document === 'undefined') return [null, show];

    /* The live region stays mounted so a screen reader hears each new
       message; only its contents come and go. */
    const node = createPortal(
        <div className="act-toast-slot" role="status" aria-live="polite">
            <AnimatePresence>
                {toast && (
                    <motion.div
                        key={toast.key}
                        className={`act-toast act-toast--${toast.tone}`}
                        initial={reduced ? { opacity: 0 } : { opacity: 0, y: -14, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={reduced ? { opacity: 0 } : { opacity: 0, y: -10, scale: 0.98 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        onClick={() => setToast(null)}
                    >
                        <span
                            className="act-toast__dot"
                            aria-hidden="true"
                            style={toast.swatch ? { background: toast.swatch } : undefined}
                        />
                        <span className="act-toast__text">{toast.text}</span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>,
        document.body
    );

    return [node, show];
}
